import { Box, Grid, pn, Stack, useElementBreakpoints } from "@compass/core";
import { CustomFilters, Select } from "components";
import { CustomEntityOption } from "components/common/CustomFilters/CustomFilter";
import { CustomFilterEntity, LkStudentDisplayFilterId, StaffFilter as StaffFilterModel, StaffFilterResource } from "generated/openApi";
import produce from "immer";
import { useEffect, useState } from "react";
import ApiService from "services/apiService";

export const StaffFilter = (props: { filter: StaffFilterModel; setFilter: (filter: StaffFilterModel) => void }) => {
  const { breakpointsRef, largeFieldBreakpoints, fullFieldBreakpoints } = useElementBreakpoints();
  const [resource, setResource] = useState<StaffFilterResource>({} as StaffFilterResource);
  const properties = resource.properties || [];

  const entityOptions: CustomEntityOption[] = [
    { EntityType: CustomFilterEntity.Staff, EntityName: "Staff" },
  ];

  useEffect(() => {
    ApiService.staff.getFilterResource().then((response) => {
      setResource(response);
    });
  }, []);

  const schoolYearField = () => {
    const propertyName = pn<StaffFilterModel>("schoolYears");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        multiple
        modelProperty={property}
        value={resource.schoolYearsWithActive?.filter((x) => props.filter.schoolYears?.includes(x.id))}
        options={resource.schoolYearsWithActive}
        getOptionLabel={(option) => option.name}
        onChange={(e, values) =>
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.schoolYears = values.map((x) => x.id);
            })
          )
        }
      />
    );
  };

  const regionField = () => {
    const propertyName = pn<StaffFilterModel>("regionIds");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        multiple
        modelProperty={property}
        value={resource.regions?.filter((x) => props.filter.regionIds?.includes(x.id))}
        options={resource.regions}
        getOptionLabel={(option) => option.name}
        onChange={(e, values) =>
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.regionIds = values.map((x) => x.id);
            })
          )
        }
      />
    );
  };

  const districtField = () => {
    const propertyName = pn<StaffFilterModel>("districtIds");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        multiple
        modelProperty={property}
        value={resource.districts?.filter((x) => props.filter.districtIds?.includes(x.id))}
        options={resource.districts}
        getOptionLabel={(option) => option.name}
        onChange={(e, values) =>
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.districtIds = values.map((x) => x.id);
            })
          )
        }
      />
    );
  };

  const schoolField = () => {
    const propertyName = pn<StaffFilterModel>("schoolIds");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        multiple
        modelProperty={property}
        value={resource.schools?.filter((x) => props.filter.schoolIds?.includes(x.id))}
        options={resource.schools}
        getOptionLabel={(option) => option.name}
        onChange={(e, values) =>
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.schoolIds = values.map((x) => x.id);
            })
          )
        }
      />
    );
  };

  const disciplineField = () => {
    const propertyName = pn<StaffFilterModel>("disciplineIds");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        multiple
        modelProperty={property}
        value={resource.disciplines?.filter((x) => props.filter.disciplineIds?.includes(x.id))}
        options={resource.disciplines}
        getOptionLabel={(option) => option.name}
        onChange={(e, values) =>
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.disciplineIds = values.map((x) => x.id);
            })
          )
        }
      />
    );
  };

  return (
    <Stack ref={breakpointsRef} spacing="lg">
      <CustomFilters
        allowedEntityTypes={entityOptions}
        filters={props.filter.customFilters}
        setFilters={(filters) =>
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.customFilters = filters;
            })
          )
        }
        properties={resource.customFilterProperties}
      />

      <Box>
        <Grid container spacing="sm">
          <Grid item xs={largeFieldBreakpoints}>
            {schoolYearField()}
          </Grid>

          {resource.canAccessRegionFilter &&
            resource.studentDisplayFilters?.find((x) => x.id === LkStudentDisplayFilterId.Region) && (
              <Grid item xs={largeFieldBreakpoints}>
                {regionField()}
              </Grid>
            )}

          {resource.canAccessDistrictFilter &&
            resource.studentDisplayFilters?.find((x) => x.id === LkStudentDisplayFilterId.District) && (
              <Grid item xs={largeFieldBreakpoints}>
                {districtField()}
              </Grid>
            )}

          {resource.canAccessSchoolFilter && (
            <Grid item xs={largeFieldBreakpoints}>
              {schoolField()}
            </Grid>
          )}

          <Grid item xs={largeFieldBreakpoints}>
            {disciplineField()}
          </Grid>
        </Grid>
      </Box>
    </Stack>
  );
};
